import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import '../../styles/OwnerReportsTab.css';
import { FiFileText, FiCheckCircle, FiClock, FiCalendar, FiRepeat } from 'react-icons/fi';
import { useToast } from '../commons/toast/ToastContext';

const OwnerReportsTab = ({ dogOwnerReports = { assignments: [], submissions: [] }, dogId }) => {
    const { t, i18n } = useTranslation('reports');
    const toast = useToast();
    const [dogReports, setDogReports] = useState({ assignments: [], submissions: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeSubmissionId, setActiveSubmissionId] = useState(null);
    const [fillModalOpen, setFillModalOpen] = useState(false);
    const [viewSubmissionOpen, setViewSubmissionOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [formAnswers, setFormAnswers] = useState({});

    // Fetch dog reports
    const fetchDogReports = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/reports/dog/${dogId}/reports`);
            setDogReports(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError(t('fetchReportsError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dogId) {
            fetchDogReports();
        }
    }, [dogId]);

    const formatDate = (dateString) => {
        if (!dateString) return t('notSpecified');
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getRecurrenceLabel = (recurrence) => {
        const recurrenceMap = {
            'ONCE': t('recurrence.once'),
            'DAILY': t('recurrence.daily'),
            'EVERY_2_DAYS': t('recurrence.every2Days'),
            'EVERY_3_DAYS': t('recurrence.every3Days'),
            'EVERY_4_DAYS': t('recurrence.every4Days'),
            'EVERY_5_DAYS': t('recurrence.every5Days'),
            'EVERY_6_DAYS': t('recurrence.every6Days'),
            'WEEKLY': t('recurrence.weekly')
        };
        return recurrenceMap[recurrence] || recurrence;
    };

    // Group submissions by assignment - backend now provides all submissions with status
    const submissionsByAssignment = dogReports.assignments.map(assignment => {
        const assignmentSubmissions = dogReports.submissions
            .filter(submission => submission.assignmentId === assignment.id)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return {
            assignment,
            submissions: assignmentSubmissions
        };
    });

    const handleSubmissionClick = (e, submissionId) => {
        setActiveSubmissionId(activeSubmissionId === submissionId ? null : submissionId);
    };

    const handleFillReport = (submission) => {
        setSelectedSubmission(submission);
        const initialAnswers = {};
        if (submission.template && submission.template.questions) {
            submission.template.questions.forEach(question => {
                initialAnswers[question.id] = '';
            });
            setFormAnswers(initialAnswers);
        }
        setFillModalOpen(true);
    };

    const handleViewSubmission = (submission) => {
        setSelectedSubmission(submission);
        setViewSubmissionOpen(true);
    };

    const handleAnswerChange = (questionId, value) => {
        setFormAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmitReport = async () => {
        try {
            setLoading(true);

            const unansweredQuestions = selectedSubmission.template.questions.filter(
                question => !formAnswers[question.id] || formAnswers[question.id].trim() === ''
            );

            if (unansweredQuestions.length > 0) {
                toast.warning(t('pleaseAnswerAllQuestions'));
                setLoading(false);
                return;
            }

            const answers = selectedSubmission.template.questions.map(question => ({
                questionId: question.id,
                answerText: formAnswers[question.id] || ''
            }));

            const submissionData = {
                submissionId: selectedSubmission.id,
                answers: answers
            };

            await axios.post('/api/v1/reports/submissions/answers', submissionData);

            setFillModalOpen(false);
            setActiveSubmissionId(null);
            toast.success(t('reportSavedSuccess'));

            // Refresh data from backend
            await fetchDogReports();
        } catch (err) {
            console.error('Error submitting report:', err);
            const errorMessage = err.response?.data?.message || t('saveReportError');
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (loading && dogReports.assignments.length === 0) return <div>{t('loadingReports')}</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="owner-reports-tab">
            {submissionsByAssignment.length === 0 ? (
                <div className="owner-reports-empty-state">
                    <p className="owner-reports-empty-title">{t('noAssignedReports')}</p>
                    <img src="/petboard-empty.png" alt="No reports" className="owner-reports-empty-image" />
                </div>
            ) : (
                <div className="owner-reports-assignments-list">
                    {submissionsByAssignment.map(({ assignment, submissions }) => {
                        return (
                            <div key={assignment.id} className="owner-reports-assignment-section">
                                <div className="owner-reports-assignment-header">
                                    <div className="owner-reports-assignment-info">
                                        <h3 className="owner-reports-assignment-template">{assignment.reportTemplateTitle}</h3>
                                        <div className="owner-reports-assignment-meta">
                                            <span className="owner-reports-recurrence-badge">
                                                <FiRepeat className="owner-reports-meta-icon" />
                                                {getRecurrenceLabel(assignment.recurrence)}
                                            </span>
                                            <span className="owner-reports-date-badge">
                                                <FiCalendar className="owner-reports-meta-icon" />
                                                {t('started')}: {formatDate(assignment.startDate)}
                                            </span>
                                            {assignment.endDate && (
                                                <span className={`owner-reports-date-badge ${
                                                    new Date(assignment.endDate) < new Date() ? 'ended' : 'active'
                                                }`}>
                                                    <FiCalendar className="owner-reports-meta-icon" />
                                                    {new Date(assignment.endDate) < new Date() ? t('ended') : t('willEnd')}: {formatDate(assignment.endDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="owner-reports-submissions-timeline">
                                    {submissions.length === 0 ? (
                                        <div className="owner-reports-no-submissions">
                                            {t('noSubmissionsAvailable')}
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="owner-reports-timeline-title">{t('reportsLabel')}:</h4>
                                            <div className="owner-reports-submissions-list">
                                                {submissions.map(submission => {
                                                    // Use server-computed status
                                                    const status = submission.status || 'PENDING';

                                                    return (
                                                        <div
                                                            key={submission.id}
                                                            className={`owner-reports-submission-item ${status.toLowerCase()} ${activeSubmissionId === submission.id ? 'active' : ''}`}
                                                            onClick={(e) => handleSubmissionClick(e, submission.id)}
                                                        >
                                                            <div className="owner-reports-submission-header">
                                                                <div className="owner-reports-status-badge-small">
                                                                    {status === 'FILLED' ? (
                                                                        <>
                                                                            <FiCheckCircle className="owner-reports-status-icon" />
                                                                            <span>{t('status.filled')}</span>
                                                                        </>
                                                                    ) : status === 'EXPIRED' ? (
                                                                        <>
                                                                            <FiClock className="owner-reports-status-icon" />
                                                                            <span>{t('status.expired')}</span>
                                                                        </>
                                                                    ) : status === 'PENDING' ? (
                                                                        <>
                                                                            <FiClock className="owner-reports-status-icon" />
                                                                            <span>{t('status.toFill')}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCalendar className="owner-reports-status-icon" />
                                                                            <span>{t('status.upcoming')}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="owner-reports-submission-date">
                                                                    <FiCalendar className="owner-reports-date-icon" />
                                                                    {formatDate(submission.dueDate)}
                                                                    {submission.completed && submission.submittedAt && (
                                                                        <span className="owner-reports-submitted-at">
                                                                            ({t('filledOn')}: {formatDate(submission.submittedAt)})
                                                                        </span>
                                                                    )}
                                                                    {status === 'EXPIRED' && (
                                                                        <span className="owner-reports-expired-note">
                                                                            ({t('taskEnded')}: {formatDate(assignment.endDate)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Submission details when clicked */}
                                                            {activeSubmissionId === submission.id && (
                                                                <div className="owner-reports-submission-details">
                                                                    <h4>{t('reportDetails')}:</h4>

                                                                    {status === 'FILLED' ? (
                                                                        <>
                                                                            <div className="owner-reports-answers-list">
                                                                                {submission.answers.map((answer, index) => (
                                                                                    <div key={answer.id} className="owner-reports-answer-item">
                                                                                        <div className="owner-reports-answer-question">
                                                                                            <span className="owner-reports-question-number">{index + 1}.</span>
                                                                                            <span className="owner-reports-question-text">{answer.questionText}</span>
                                                                                        </div>
                                                                                        <div className="owner-reports-answer-text">
                                                                                            {answer.answerText || <em>{t('noAnswer')}</em>}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                            <div className="owner-reports-view-report-actions">
                                                                                <button
                                                                                    className="owner-reports-view-full-report-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleViewSubmission(submission);
                                                                                    }}
                                                                                >
                                                                                    {t('viewFullReport')}
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    ) : status === 'EXPIRED' ? (
                                                                        <div className="owner-reports-expired-report">
                                                                            <p>{t('reportExpiredMessage')}</p>
                                                                        </div>
                                                                    ) : status === 'PENDING' ? (
                                                                        <div className="owner-reports-report-actions">
                                                                            <p>{t('reportPendingFill')}</p>
                                                                            <button
                                                                                className="owner-reports-fill-report-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleFillReport(submission);
                                                                                }}
                                                                            >
                                                                                {t('fillNow')}
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="owner-reports-no-answers">
                                                                            {t('reportAvailableFuture')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Fill Report Modal */}
            {fillModalOpen && selectedSubmission && (
                <div className="owner-reports-modal-overlay">
                    <div className="owner-reports-modal owner-reports-large-modal">
                        <div className="owner-reports-modal-header">
                            <h2 className="owner-reports-modal-title">{t('fillReport')}: {selectedSubmission.template.title}</h2>
                            <button
                                className="owner-reports-close-btn"
                                onClick={() => setFillModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="owner-reports-modal-content">
                            <div className="owner-reports-report-meta">
                                <span>{t('fillDate')}: {formatDate(selectedSubmission.dueDate)}</span>
                            </div>

                            {selectedSubmission.template.questions && selectedSubmission.template.questions.length > 0 ? (
                                <div className="owner-reports-questions-list">
                                    {selectedSubmission.template.questions
                                        .sort((a, b) => a.orderIndex - b.orderIndex)
                                        .map((question, index) => (
                                            <div key={question.id} className="owner-reports-question-item">
                                                <div className="owner-reports-question-header">
                                                    <div className="owner-reports-question-number">{index + 1}.</div>
                                                    <div className="owner-reports-question-text">{question.questionText}</div>
                                                </div>
                                                <div className="owner-reports-answer-input">
                                                    <textarea
                                                        placeholder={t('typeYourAnswer')}
                                                        value={formAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        rows="3"
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="owner-reports-empty-questions-message">
                                    {t('noQuestions')}
                                </div>
                            )}
                        </div>

                        <div className="owner-reports-modal-footer">
                            <button
                                className="owner-reports-cancel-btn"
                                onClick={() => setFillModalOpen(false)}
                                disabled={loading}
                            >
                                {t('cancel')}
                            </button>
                            {selectedSubmission.template.questions && selectedSubmission.template.questions.length > 0 && (
                                <button
                                    className="owner-reports-save-btn"
                                    onClick={handleSubmitReport}
                                    disabled={loading}
                                >
                                    {loading ? t('saving') : t('saveReport')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* View Full Report Modal */}
            {viewSubmissionOpen && selectedSubmission && (
                <div className="owner-reports-modal-overlay">
                    <div className="owner-reports-modal owner-reports-large-modal">
                        <div className="owner-reports-modal-header">
                            <h2 className="owner-reports-modal-title">{t('report')}: {selectedSubmission.template.title}</h2>
                            <button
                                className="owner-reports-close-btn"
                                onClick={() => setViewSubmissionOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="owner-reports-modal-content">
                            <div className="owner-reports-submission-meta">
                                <div className="owner-reports-meta-row">
                                    <span className="owner-reports-meta-label">{t('reportDate')}:</span>
                                    <span className="owner-reports-meta-value">{formatDate(selectedSubmission.dueDate)}</span>
                                </div>
                                {selectedSubmission.submittedAt && (
                                    <div className="owner-reports-meta-row">
                                        <span className="owner-reports-meta-label">{t('fillDate')}:</span>
                                        <span className="owner-reports-meta-value">{formatDate(selectedSubmission.submittedAt)}</span>
                                    </div>
                                )}
                                <div className="owner-reports-meta-row">
                                    <span className="owner-reports-meta-label">{t('statusLabel')}:</span>
                                    <span className="owner-reports-meta-value completed">
                                        <FiCheckCircle className="owner-reports-status-icon-small" /> {t('status.filled')}
                                    </span>
                                </div>
                            </div>

                            <div className="owner-reports-answers-section">
                                <h3>{t('yourAnswers')}:</h3>
                                <div className="owner-reports-answers-list">
                                    {selectedSubmission.answers.map((answer, index) => (
                                        <div key={answer.id} className="owner-reports-answer-item">
                                            <div className="owner-reports-answer-question">
                                                <span className="owner-reports-question-number">{index + 1}.</span>
                                                <span className="owner-reports-question-text">{answer.questionText}</span>
                                            </div>
                                            <div className="owner-reports-answer-text">
                                                {answer.answerText || <em>{t('noAnswer')}</em>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="owner-reports-modal-footer">
                            <button
                                className="owner-reports-cancel-btn"
                                onClick={() => setViewSubmissionOpen(false)}
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerReportsTab;
